import type { ReactNode } from "react";

import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

export interface BarSegment {
  key: string;
  label: string;
  /** Classe Tailwind de fundo — o vocabulário da origem é `bar`, não `tone`. */
  className: string;
  value: number;
}

interface StackedBarProps {
  segments: BarSegment[];
  /** Classe de altura, como no call site da aderência (`h-5` default). */
  height?: string;
  /**
   * `horizontal` (default) preserva a origem byte a byte. `vertical` é a coluna
   * do catálogo WRICEF do DIAG.3: empilha de baixo para cima, com o primeiro
   * segmento do array no **topo** (é onde o pior fica).
   */
  orientation?: "horizontal" | "vertical";
  /**
   * Conteúdo dentro do segmento — o número na barra de licenciamento. Opcional:
   * sem ele o segmento continua sendo a `div` vazia da origem.
   */
  renderSegment?: (segment: BarSegment) => ReactNode;
  /**
   * Desenha a legenda abaixo da barra, com o valor de cada segmento.
   *
   * Ela existe por causa do {@link MIN_SHARE_FOR_LABEL}: a fatia fina deixa de
   * pintar o número, e sem a legenda ele sumiria da tela. Inclui segmento
   * zerado, porque zero medido é resposta e a barra não tem como desenhar
   * largura zero.
   */
  legend?: boolean;
  className?: string;
}

/**
 * Fatia abaixo disto não tenta desenhar o rótulo.
 *
 * Medido na tela: 205 executados contra 32.550 não medidos dá 0,6% de largura,
 * e o `overflow-hidden` corta o número no meio de um dígito. Meio dígito é pior
 * que dígito nenhum — inventa um número que ninguém escreveu. O valor continua
 * no `title`, no `aria-label` e, quando pedida, na legenda.
 */
const MIN_SHARE_FOR_LABEL = 0.08;

/**
 * Extraído de `aderencia/OverviewView`, generalizado: os segmentos passam a vir
 * por prop em vez de sair de `SUGGESTION_ORDER`/`SUGGESTION_META` importados no
 * módulo.
 *
 * Preservados da origem: `role="img"` com `aria-label` composto de **todos** os
 * segmentos (inclusive os zerados, que são informação para leitor de tela), o
 * layout `flexGrow: n / flexBasis: 0`, a omissão visual de segmento `<= 0` e o
 * track vazio quando o total é zero.
 *
 * `orientation` e `renderSegment` são adições do DIAG.3, **opcionais**: omitidas,
 * o componente renderiza exatamente o que os três call sites originais já
 * recebiam. É o que mantém `primitives-regression.test.tsx` verde.
 */
export function StackedBar({
  segments,
  height = "h-5",
  orientation = "horizontal",
  renderSegment,
  legend = false,
  className,
}: StackedBarProps) {
  const total = segments.reduce((acc, s) => acc + (s.value ?? 0), 0);
  const vertical = orientation === "vertical";

  const bar = (
    <div
      className={cn(
        "flex overflow-hidden rounded bg-muted",
        // Na vertical a altura vem por `height` e a largura é do pai; na
        // horizontal é o inverso, como na origem.
        vertical ? "h-full w-full flex-col justify-end" : "w-full",
        height,
        className,
      )}
      role="img"
      aria-label={segments.map((s) => `${s.value ?? 0} ${s.label}`).join(", ")}
    >
      {segments.map((s) => {
        const n = s.value ?? 0;
        if (n <= 0) return null;
        return (
          <div
            key={s.key}
            data-segment={s.key}
            className={cn(
              s.className,
              renderSegment && "flex items-center justify-center overflow-hidden",
            )}
            style={{ flexGrow: n, flexBasis: 0 }}
            title={`${n} ${s.label}`}
          >
            {total > 0 && n / total >= MIN_SHARE_FOR_LABEL ? renderSegment?.(s) : null}
          </div>
        );
      })}
      {total === 0 && <div data-empty-track="true" className="flex-1" />}
    </div>
  );

  if (!legend) return bar;

  return (
    <div className="w-full">
      {bar}
      <ul
        data-testid="stacked-bar-legend"
        className="mt-2 flex flex-wrap gap-x-4 gap-y-1"
      >
        {segments.map((s) => (
          <li key={s.key} className="flex items-center gap-1.5 text-[11px] text-[#596b85]">
            <span className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", s.className)} />
            <span>
              {s.label} <strong className="font-semibold tabular-nums">{formatCount(s.value ?? 0)}</strong>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
